import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Lock, ShieldCheck, FileKey, Trash2, EyeOff, Server } from 'lucide-react';

const pillars = [
  {
    icon: Lock,
    title: 'Connexion chiffrée',
    desc: 'Tous les échanges entre ton navigateur et nos serveurs passent en HTTPS / TLS.',
  },
  {
    icon: FileKey,
    title: 'Mot de passe haché',
    desc: 'On ne stocke jamais ton mot de passe en clair : on garde une empreinte (bcrypt) impossible à inverser.',
  },
  {
    icon: Server,
    title: 'Tes documents te restent',
    desc: 'Tes fichiers sont rangés dans un dossier dédié à ton compte, jamais partagés avec d\'autres utilisateurs.',
  },
  {
    icon: EyeOff,
    title: 'OCR en local',
    desc: 'La lecture de tes documents (Tesseract) tourne sur nos serveurs — aucun envoi à un service tiers.',
  },
  {
    icon: Trash2,
    title: 'Suppression à la demande',
    desc: 'Tu peux supprimer ton compte et tes données à tout moment. Conformité RGPD garantie.',
  },
  {
    icon: ShieldCheck,
    title: 'FranceConnect prévu',
    desc: 'Bientôt, identifie-toi avec ton compte impôts ou ameli — sans qu\'on stocke d\'identifiants sensibles.',
  },
];

export const SecuritySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="securite"
      className={`section-apple px-6 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#F8F7F4]'}`}
      data-testid="security-section"
    >
      <div className="max-w-[980px] mx-auto">
        {/* Title */}
        <motion.div
          ref={ref}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[13px] tracking-[0.02em] uppercase mb-4 text-[#81d8d0]">
            Confiance & sécurité
          </p>
          <h2
            className={`text-[36px] sm:text-[44px] font-semibold tracking-[-0.03em] leading-[1.1] mb-4 ${
              isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
            }`}
          >
            Comment Skipya
            <br />
            <span className="text-[#81d8d0]">protège tes données.</span>
          </h2>
          <p
            className={`text-[16px] max-w-[600px] mx-auto ${
              isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
            }`}
          >
            On gère du sensible (revenus, documents, identité). Voici exactement ce qu'on fait pour que tu sois tranquille.
          </p>
        </motion.div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }}
                className={`p-5 rounded-2xl ${isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-black/5'} border`}
                data-testid={`security-pillar-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#81d8d0]/15 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#81d8d0]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div
                      className={`font-medium text-[15px] mb-1 ${
                        isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
                      }`}
                    >
                      {p.title}
                    </div>
                    <p
                      className={`text-[13px] leading-[1.5] ${
                        isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
                      }`}
                    >
                      {p.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;
